/** Lead list inside a campaign — Phase A of CAMPAIGN_LEADS_QUESTIONS.md.
 *
 * Three actions in the header: Download Excel template, Import .xlsx,
 * New lead (single form). Body is a status-filterable table of leads.
 * No detail page yet — status changes happen inline via a per-row
 * select. Detail page lands in Phase B alongside the Q&A panel.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useEffect, useRef, useState } from "react";

import { DateWindowPicker, SortSelect } from "../../components/ListSortAndDate";
import {
  emptyWindow,
  windowParams,
  type DateWindow,
} from "../../components/listParams";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { BeneficiaryFormFields } from "@/components/BeneficiaryFormFields";
import {
  beneficiaryFormFromContact,
  beneficiaryPayload,
  canSubmitBeneficiary,
  emptyBeneficiaryForm,
  type BeneficiaryForm,
} from "@/components/beneficiaryForm";
import { Pagination } from "@/components/Pagination";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import { displayPhone } from "@/lib/phone";
import type {
  Contact,
  ImportJob as ImportJobType,
  Lead,
  LeadStatus,
  Paginated,
} from "@/lib/types";

import { BeneficiaryDetailDrawer, LeadStatusPill } from "./BeneficiaryDetailDrawer";
import { ContactedByBadge } from "./ContactedBy";
import { ScheduleLeadModal } from "./ScheduleLeadModal";
import { BulkAttachBeneficiariesModal } from "./BulkAttachBeneficiariesModal";
import { resolveFieldSchema } from "./api";
import { ContactAddresses } from "./ContactAddresses";
import { ImportMappingModal } from "./ImportMappingModal";
import { ImportReviewModal } from "./ImportReviewModal";
import { DECLINED_STATUSES, STATUS_STYLES, STATUS_VALUES } from "./leadStatus";
import { MembershipFieldsCard, MembershipFieldsFieldset } from "./MembershipFieldsEditor";


function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}


type Props = {
  campaignId: string;
  campaignName: string;
};

const LEADS_PAGE_SIZE = 30;

export function CampaignLeads({ campaignId, campaignName }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [statusFilter, setStatusFilter] = useState<LeadStatus | "">("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkAttach, setShowBulkAttach] = useState(false);
  // 2026-09-17 redesign — the list row is a compact summary; clicking it
  // opens the detail drawer, whose own buttons open these same modals
  // (previously each row hand-rolled its own show*/setShow* state).
  const [detailLead, setDetailLead] = useState<Lead | null>(null);
  const [editLeadId, setEditLeadId] = useState<string | null>(null);
  const [scheduleLeadId, setScheduleLeadId] = useState<string | null>(null);
  const [removeLeadId, setRemoveLeadId] = useState<string | null>(null);
  // Phase 2E-camp-mr — surface archived rows in an opt-in "Archived"
  // view so operators can spot+restore them without bypassing the
  // default active-only filter.
  const [includeArchived, setIncludeArchived] = useState(false);
  // Backlog (2026-09-16) — "who do I need to call today". Narrows the
  // list to beneficiaries with a non-terminal session scheduled inside
  // today (server resolves "today" against the campaign's own timezone,
  // not the browser's — see MembershipListView).
  const [scheduledToday, setScheduledToday] = useState(false);
  // Requested 2026-09-23 — a multi-site beneficiary is a different job
  // (and, once per-site pricing lands, a different price), so isolating
  // them matters.
  const [multisiteOnly, setMultisiteOnly] = useState(false);
  // "" = any. Who DECIDED to reach them, not which code path ran.
  const [contactedBy, setContactedBy] = useState("");
  // Sorting + "who did I contact / see on <day>" (2026-09-29).
  const [sort, setSort] = useState("-created");
  const [contactedWindow, setContactedWindow] = useState<DateWindow>(emptyWindow);
  const [sessionWindow, setSessionWindow] = useState<DateWindow>(emptyWindow);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [
    statusFilter,
    search,
    includeArchived,
    scheduledToday,
    multisiteOnly,
    sort,
    contactedWindow,
    sessionWindow,
  ]);

  const leads = useQuery({
    queryKey: [
      "campaign-leads",
      campaignId,
      statusFilter,
      search,
      includeArchived,
      scheduledToday,
      multisiteOnly,
      sort,
      contactedBy,
      contactedWindow,
      sessionWindow,
      page,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        page_size: String(LEADS_PAGE_SIZE),
      };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      if (includeArchived) params.include_archived = "1";
      if (scheduledToday) params.scheduled = "today";
      if (multisiteOnly) params.multisite = "true";
      if (sort) params.sort = sort;
      if (contactedBy) params.contacted_by = contactedBy;
      Object.assign(params, windowParams("contacted", contactedWindow));
      Object.assign(params, windowParams("session", sessionWindow));
      const res = await api.get<Paginated<Lead>>(
        ApiPaths.campaignLeads(campaignId),
        { params },
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
  const leadRows = leads.data?.results ?? [];
  const leadTotal = leads.data?.count ?? 0;

  const downloadTemplate = useMutation({
    mutationFn: async (withQuestions: boolean) => {
      const res = await api.get(ApiPaths.campaignLeadTemplate(campaignId), {
        responseType: "blob",
        params: withQuestions ? { with_questions: 1 } : undefined,
      });
      const safeName = campaignName
        .replace(/[^a-z0-9 _-]/gi, "")
        .slice(0, 40)
        .trim();
      const suffix = withQuestions ? "-with-questions" : "";
      downloadBlob(
        res.data,
        `nexbasira-leads${suffix}-${safeName || campaignId.slice(0, 8)}.xlsx`,
      );
    },
  });

  // P7 — POST → 202 + {job_id}. Upload no longer starts the import: the
  // job parks in `awaiting_mapping` and we open the mapping modal so the
  // operator can say what each column of THEIR file is. Confirming there
  // dispatches the worker; from that point we track the active job_id
  // and poll its status until terminal, rendering the live status block
  // + the success / row-errors / fatal payload that lands on the job.
  const [mappingJobId, setMappingJobId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // The job stops a second time when its rows disagree about a
  // beneficiary — nothing is written until the operator decides. See
  // ImportReviewModal / docs/IMPORT_CONFLICT_RESOLVER.md.
  const [reviewJobId, setReviewJobId] = useState<string | null>(null);
  // Deep link from Import history: ?review=<job id>. The history page is
  // where an operator lands after a reload, and a parked job has to be
  // finishable from there.
  const [urlParams, setUrlParams] = useSearchParams();
  const reviewParam = urlParams.get("review");
  useEffect(() => {
    if (!reviewParam) return;
    setReviewJobId(reviewParam);
    // Consume the parameter so a refresh doesn't reopen a modal for a
    // job the operator has already dealt with.
    const next = new URLSearchParams(urlParams);
    next.delete("review");
    setUrlParams(next, { replace: true });
  }, [reviewParam, urlParams, setUrlParams]);
  // Test-import escape hatch: reuse one real phone across many synthetic
  // beneficiaries (email still dedups). Off by default.
  const [allowDupPhone, setAllowDupPhone] = useState(false);
  const importFile = useMutation({
    mutationFn: async ({
      file,
      allowDuplicatePhone,
    }: {
      file: File;
      allowDuplicatePhone: boolean;
    }) => {
      const form = new FormData();
      form.append("file", file);
      if (allowDuplicatePhone) form.append("allow_duplicate_phone", "true");
      const res = await api.post<{ job_id: string; status: string }>(
        ApiPaths.campaignLeadImport(campaignId),
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return res.data;
    },
    onSuccess: (data) => {
      setMappingJobId(data.job_id);
    },
  });

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    importFile.mutate({ file: f, allowDuplicatePhone: allowDupPhone });
    // Reset so the same file can be re-picked after a fix.
    e.target.value = "";
  }

  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("leads.sectionTitle")}
          </h2>
          <p className="text-xs text-nb-text-3">{t("leads.sectionHint")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => downloadTemplate.mutate(false)}
            disabled={downloadTemplate.isPending}
          >
            {downloadTemplate.isPending
              ? t("leads.downloadingTemplate")
              : t("leads.downloadTemplate")}
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => fileRef.current?.click()}
            disabled={importFile.isPending}
          >
            {importFile.isPending ? t("leads.importing") : t("leads.importExcel")}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={onFilePicked}
          />
          <label
            className="flex items-center gap-1.5 text-xs text-nb-text-3"
            title={t("leads.allowDupPhoneHint")}
          >
            <input
              type="checkbox"
              checked={allowDupPhone}
              onChange={(e) => setAllowDupPhone(e.target.checked)}
            />
            {t("leads.allowDupPhone")}
          </label>
          <Link
            to={`/campaigns/${campaignId}/imports`}
            className="btn-ghost text-sm"
          >
            {t("leads.importHistory", "Import history")}
          </Link>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setShowBulkAttach(true)}
          >
            {t("leads.addFromList", "Add from beneficiaries")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => setShowCreate(true)}
          >
            {t("leads.newLead")}
          </button>
        </div>
      </header>

      {showBulkAttach && (
        <BulkAttachBeneficiariesModal
          campaignId={campaignId}
          onClose={() => setShowBulkAttach(false)}
        />
      )}

      {importFile.isError && <ImportErrorBlock error={importFile.error} />}
      {mappingJobId && (
        <ImportMappingModal
          campaignId={campaignId}
          jobId={mappingJobId}
          onCancel={() => setMappingJobId(null)}
          onConfirmed={() => {
            setActiveJobId(mappingJobId);
            setMappingJobId(null);
          }}
        />
      )}
      {reviewJobId && (
        <ImportReviewModal
          campaignId={campaignId}
          jobId={reviewJobId}
          onCancel={() => setReviewJobId(null)}
          onResolved={(next) => {
            setReviewJobId(null);
            // "remap" sends the job BACK to column mapping — pointing a
            // column at a per-site custom field is a mapping change, not
            // a review one.
            if (next === "awaiting_mapping") {
              setMappingJobId(activeJobId);
              setActiveJobId(null);
            }
          }}
        />
      )}
      {activeJobId && (
        <ImportJobBlock
          campaignId={campaignId}
          jobId={activeJobId}
          onNeedsReview={() => setReviewJobId(activeJobId)}
          onDone={() => {
            void qc.invalidateQueries({
              queryKey: ["campaign-leads", campaignId],
            });
            void qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
            void qc.invalidateQueries({ queryKey: ["contacts"] });
          }}
          onDismiss={() => setActiveJobId(null)}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input max-w-[14rem]"
          aria-label={t("leads.statusFilterLabel")}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as LeadStatus | "")}
        >
          <option value="">{t("leads.statusFilterAll")}</option>
          {STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {t(`leads.status.${s}`)}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="input max-w-[18rem]"
          placeholder={t("leads.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className={
            scheduledToday
              ? "pill bg-nb-mint/20 text-nb-mint"
              : "btn-ghost text-xs"
          }
          aria-pressed={scheduledToday}
          onClick={() => setScheduledToday((v) => !v)}
          data-testid="scheduled-today-toggle"
        >
          📞 {t("leads.scheduledTodayFilter", "Call today")}
        </button>
        <button
          type="button"
          className={
            multisiteOnly
              ? "pill bg-nb-mint/20 text-nb-mint"
              : "btn-ghost text-xs"
          }
          aria-pressed={multisiteOnly}
          onClick={() => setMultisiteOnly((v) => !v)}
          data-testid="multisite-toggle"
        >
          {t("leads.multisiteFilter", "Multi-site")}
        </button>
        <label className="flex items-center gap-1.5 text-xs text-nb-text-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-600 bg-nb-surface text-nb-mint focus:ring-nb-mint"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="include-archived-toggle"
          />
          {t("leads.includeArchived", "Show archived")}
        </label>
        <SortSelect
          value={sort}
          onChange={setSort}
          options={[
            "created",
            "contacted",
            // Requested 2026-10-02 — the appointment is on every row and
            // was the one column the list could not be ordered by.
            "scheduled",
            "session_created",
            "session_ended",
            "session_started",
          ]}
        />
        <DateWindowPicker
          label={t("lists.contactedFilter")}
          value={contactedWindow}
          onChange={setContactedWindow}
          /* A blank `last_contacted_at` is "no record", not "never" —
             a booking-invite email was logged nowhere before the field
             existed, so the filter cannot see those. Saying so beats
             letting an operator read an empty result as "nobody". */
          hint={t("lists.noRecordHint")}
        />
        <label className="space-y-1 text-xs text-nb-text-2">
          <span className="block font-mono text-[0.65rem] uppercase tracking-wider text-nb-text-3">
            {t("leads.contactedByLabel")}
          </span>
          <select
            className="input py-1 text-sm"
            value={contactedBy}
            onChange={(e) => {
              setContactedBy(e.target.value);
              setPage(1);
            }}
            data-testid="contacted-by-filter"
          >
            <option value="">{t("leads.contactedByAny")}</option>
            <option value="system">{t("leads.contactedBySystem")}</option>
            <option value="operator">{t("leads.contactedByOperator")}</option>
          </select>
        </label>
        <DateWindowPicker
          label={t("lists.sessionFilter")}
          value={sessionWindow}
          onChange={setSessionWindow}
        />
      </div>
      {scheduledToday && (
        <p className="text-xs text-nb-text-3">
          {t(
            "leads.scheduledTodayNote",
            "Showing only beneficiaries with an open call scheduled today.",
          )}
        </p>
      )}

      {leads.isLoading && (
        <p className="card text-sm text-nb-text-3">{t("common.loading")}</p>
      )}
      {leads.data && leadTotal === 0 && (
        <p className="card text-sm text-nb-text-3">{t("leads.empty")}</p>
      )}
      {leadRows.length > 0 && (
        <ul className="space-y-2">
          {leadRows.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              campaignId={campaignId}
              onOpen={() => setDetailLead(lead)}
            />
          ))}
        </ul>
      )}

      {detailLead && (
        <BeneficiaryDetailDrawer
          key={detailLead.id}
          lead={
            // Keep the drawer in sync with the list's own refetch (status
            // change, restore, etc. invalidate ["campaign-leads", ...]) —
            // fall back to the originally-clicked row if it briefly drops
            // out of the current page/filter.
            leadRows.find((r) => r.id === detailLead.id) ?? detailLead
          }
          campaignId={campaignId}
          onClose={() => setDetailLead(null)}
          onEdit={() => setEditLeadId(detailLead.id)}
          onSchedule={() => setScheduleLeadId(detailLead.id)}
          onRemove={() => setRemoveLeadId(detailLead.id)}
        />
      )}
      {editLeadId && (
        <EditBeneficiaryModal
          lead={leadRows.find((r) => r.id === editLeadId) ?? detailLead!}
          campaignId={campaignId}
          onClose={() => setEditLeadId(null)}
        />
      )}
      {scheduleLeadId && (
        <ScheduleLeadModal
          lead={leadRows.find((r) => r.id === scheduleLeadId) ?? detailLead!}
          campaignId={campaignId}
          onClose={() => setScheduleLeadId(null)}
        />
      )}
      {removeLeadId && (
        <RemoveLeadModal
          lead={leadRows.find((r) => r.id === removeLeadId) ?? detailLead!}
          campaignId={campaignId}
          onClose={() => setRemoveLeadId(null)}
        />
      )}

      <Pagination
        page={page}
        pageSize={LEADS_PAGE_SIZE}
        count={leadTotal}
        onPage={setPage}
        disabled={leads.isFetching}
      />

      {showCreate && (
        <NewLeadModal
          campaignId={campaignId}
          onClose={() => setShowCreate(false)}
        />
      )}
    </section>
  );
}


type ImportErrorPayload = {
  detail?: string;
  row_errors?: { excel_row: number; field: string; message: string }[];
  warnings?: string[];
};

const TERMINAL_JOB_STATUSES = new Set<ImportJobType["status"]>([
  "succeeded",
  "failed_validation",
  "failed_fatal",
  "error",
]);

// Not terminal — the job is alive and will continue — but it is waiting
// on a human, so polling every 1.5s while they think is a hammer for no
// information. The review modal invalidates the job query when it
// submits.
//
// `awaiting_mapping` is deliberately NOT here. This block mounts the
// instant the mapping POST returns, and its first fetch can still
// observe the pre-confirmation status; treating that as "stop" left the
// query parked forever, so the job reached awaiting_review on the server
// and the client never saw it. Found on the first real import.
const WAITING_ON_OPERATOR = new Set<ImportJobType["status"]>([
  "awaiting_review",
]);


/**
 * Polls the ImportJob row until it reaches a terminal state. Surfaces
 * a live status pill while the worker chews; on completion renders
 * either a green success block or a red error block (with the row
 * errors + a "Download error workbook" button when one is available).
 */
function ImportJobBlock({
  campaignId,
  jobId,
  onDone,
  onDismiss,
  onNeedsReview,
}: {
  campaignId: string;
  jobId: string;
  onDone: () => void;
  onDismiss: () => void;
  onNeedsReview: () => void;
}) {
  const { t } = useTranslation();
  const job = useQuery({
    queryKey: ["campaign-import-job", campaignId, jobId],
    queryFn: async () => {
      const res = await api.get<ImportJobType>(
        ApiPaths.campaignImport(campaignId, jobId),
      );
      return res.data;
    },
    refetchInterval: (q) => {
      const data = q.state.data as ImportJobType | undefined;
      // No data yet can mean "first load" OR "this request keeps
      // failing" (a 429, a deleted job). Polling forever in the latter
      // case is how a transient error turns into a permanent hammer, so
      // give up after a bounded number of empty attempts.
      if (!data) return q.state.fetchFailureCount > 5 ? false : 1500;
      if (WAITING_ON_OPERATOR.has(data.status)) return false;
      return TERMINAL_JOB_STATUSES.has(data.status) ? false : 1500;
    },
  });

  // Invalidate the campaign caches exactly ONCE, when the job first
  // reaches `succeeded` with something actually imported. Must be an
  // effect: doing this during render re-entered itself forever (see the
  // note at the `succeeded` branch below).
  const doneFired = useRef(false);
  const jobStatus = job.data?.status;

  // Open the review gate as soon as the worker parks the job. Effect,
  // not render — same re-entrancy trap as the `succeeded` branch below.
  const reviewFired = useRef(false);
  useEffect(() => {
    if (reviewFired.current) return;
    if (jobStatus !== "awaiting_review") return;
    reviewFired.current = true;
    onNeedsReview();
  }, [jobStatus, onNeedsReview]);
  const createdCount =
    (job.data?.memberships_created ?? 0) + (job.data?.contacts_created ?? 0);
  useEffect(() => {
    if (doneFired.current) return;
    if (jobStatus !== "succeeded" || createdCount <= 0) return;
    doneFired.current = true;
    onDone();
  }, [jobStatus, createdCount, onDone]);

  const downloadError = useMutation({
    mutationFn: async () => {
      // Endpoint returns `{"url": presigned}`. Use plain `fetch` (no
      // auth header) for the second hop so the presigned signature is
      // MinIO's only credential, then trigger the download via an
      // `<a download>` element — popup blockers kill a `window.open`
      // that runs after an `await` because it's no longer inside a
      // synchronous user gesture.
      const res = await api.get<{ url?: string }>(
        ApiPaths.campaignImportErrorFile(campaignId, jobId),
      );
      if (!res.data?.url) return;
      const fileRes = await fetch(res.data.url);
      if (!fileRes.ok) return;
      const blob = await fileRes.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `import-${jobId}-errors.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    },
  });

  if (job.isLoading || !job.data) {
    return (
      <div className="card border-nb-border bg-nb-surface/40 text-sm text-nb-text-2">
        {t("leads.import.queued", "Queued — waiting for the worker…")}
      </div>
    );
  }

  const data = job.data;

  // Parked for conflict review. The modal opens itself when the status
  // first arrives, but a reload — or dismissing it — would otherwise
  // strand the import with no route back in, which is exactly what
  // happened on the first real run. Always offer the door.
  if (data.status === "awaiting_review") {
    return (
      <div
        className="card space-y-2 border-nb-mint/40 bg-nb-surface/40 text-sm"
        data-testid="awaiting-review-card"
      >
        <p className="font-medium text-nb-text">
          {t("leads.import.reviewNeeded")}
        </p>
        <p className="text-xs text-nb-text-3">
          {t("leads.import.reviewNeededHint")}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={onNeedsReview}
            data-testid="open-review"
          >
            {t("leads.import.openReview")}
          </button>
          <button type="button" className="btn-ghost text-sm" onClick={onDismiss}>
            {t("common.dismiss")}
          </button>
        </div>
      </div>
    );
  }

  const terminal = TERMINAL_JOB_STATUSES.has(data.status);
  if (!terminal) {
    return (
      <div className="card border-nb-border bg-nb-surface/40 space-y-1 text-sm">
        <p className="font-medium text-nb-text">
          {t(`leads.import.status.${data.status}`, data.status)}
        </p>
        <p className="text-xs text-nb-text-3">
          {t(
            "leads.import.pollingHint",
            "Refreshing every 1.5s; this card updates automatically.",
          )}
        </p>
      </div>
    );
  }

  if (data.status === "succeeded") {
    // NOTE: the cache-invalidation hook is fired from an effect above,
    // NOT from here. Calling it during render invalidated the very
    // queries whose refetch re-rendered this component, which called it
    // again — an unbounded loop that hammered
    // `/campaigns/<id>` + `/campaigns/<id>/memberships` several times a
    // second and burned the whole per-user rate limit (18k HTTP 429s in
    // three hours on prod, 2026-09-22). Every other request the
    // operator made — Call Now from the beneficiary drawer included —
    // then failed as collateral.
    return (
      <div className="card border-cvp-ok/40 bg-cvp-ok/10 space-y-1.5">
        <div className="flex items-start justify-between">
          <p className="text-sm font-medium text-nb-text">
            {t("leads.importedCount", {
              count: data.memberships_created,
            })}
          </p>
          <button
            type="button"
            className="text-xs text-nb-text-3 hover:underline"
            onClick={onDismiss}
          >
            {t("common.dismiss", "Dismiss")}
          </button>
        </div>
        <p className="text-xs text-nb-text-2">
          {t("leads.importBreakdown", {
            newCount: data.contacts_created,
            updatedCount: data.contacts_updated,
            membershipsCount: data.memberships_created,
          })}
        </p>
        {data.skipped_jsonb && data.skipped_jsonb.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-cvp-warn">
              {t("leads.importSkippedSummary", {
                count: data.skipped_jsonb.length,
              })}
            </summary>
            <ul className="mt-1 ml-3 list-disc space-y-0.5 text-nb-text-2">
              {data.skipped_jsonb.slice(0, 30).map((r, i) => (
                <li key={i}>
                  {r.first_name} {r.last_name}{" "}
                  <span className="text-nb-text-3">
                    ({t(`leads.skipReason.${r.reason}`, r.reason)})
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
        {data.warnings_jsonb && data.warnings_jsonb.length > 0 && (
          <p className="text-xs text-cvp-warn">
            {data.warnings_jsonb.join("; ")}
          </p>
        )}
      </div>
    );
  }

  // failed_validation / failed_fatal / error
  const detail =
    data.status === "failed_fatal"
      ? data.fatal_error ||
        t("leads.import.fatalGeneric", "File rejected before validation.")
      : data.status === "error"
        ? data.fatal_error ||
          t("leads.import.workerErrorGeneric", "Worker error — please retry.")
        : t("leads.import.validationFailed", {
            count: data.error_count,
            defaultValue:
              "{{count}} row(s) failed validation; fix and reupload.",
          });

  return (
    <div className="card border-cvp-danger/40 bg-cvp-danger/10 space-y-2">
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-cvp-danger">{detail}</p>
        <button
          type="button"
          className="text-xs text-nb-text-3 hover:underline"
          onClick={onDismiss}
        >
          {t("common.dismiss", "Dismiss")}
        </button>
      </div>
      {data.has_error_file && (
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => downloadError.mutate()}
          disabled={downloadError.isPending}
        >
          {t(
            "leads.import.downloadErrorFile",
            "Download error report (.xlsx)",
          )}
        </button>
      )}
      {data.errors_jsonb && data.errors_jsonb.length > 0 && (
        <ul className="mt-1 max-h-48 overflow-y-auto text-xs text-nb-text-2">
          {data.errors_jsonb.slice(0, 50).map((r, i) => (
            <li key={i}>
              <span className="font-mono">row {r.excel_row}</span> ·{" "}
              <span className="text-nb-text-3">{r.field}</span>:{" "}
              <span>{r.message}</span>
            </li>
          ))}
          {data.errors_jsonb.length > 50 && (
            <li className="mt-1 text-nb-text-3">
              … and {data.errors_jsonb.length - 50} more
            </li>
          )}
        </ul>
      )}
      {data.warnings_jsonb && data.warnings_jsonb.length > 0 && (
        <p className="text-xs text-cvp-warn">
          {data.warnings_jsonb.join("; ")}
        </p>
      )}
    </div>
  );
}


function ImportErrorBlock({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const payload: ImportErrorPayload | undefined =
    error && typeof error === "object" && "response" in error
      ? (
          (
            error as { response?: { data?: ImportErrorPayload } }
          ).response?.data
        )
      : undefined;
  const detail =
    payload?.detail ?? describeApiError(error, t("leads.importFailedGeneric"));
  const rows = payload?.row_errors ?? [];
  return (
    <div className="card border-cvp-danger/40 bg-cvp-danger/10">
      <p className="text-sm font-medium text-cvp-danger">{detail}</p>
      {rows.length > 0 && (
        <ul className="mt-2 max-h-48 overflow-y-auto text-xs text-nb-text-2">
          {rows.slice(0, 50).map((r, i) => (
            <li key={i}>
              <span className="font-mono">row {r.excel_row}</span> ·{" "}
              <span className="text-nb-text-3">{r.field}</span>:{" "}
              <span>{r.message}</span>
            </li>
          ))}
          {rows.length > 50 && (
            <li className="mt-1 text-nb-text-3">
              … and {rows.length - 50} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}


/** Compact summary row — 2026-09-17 redesign. Used to show contact,
 *  city, project, a live status <select>, and three action buttons all
 *  at once ("too much and killing my eyes"). Now just the essentials;
 *  clicking anywhere opens <BeneficiaryDetailDrawer> with everything
 *  else (contact/address/custom fields, actions, and the sessions +
 *  communications history).
 *
 *  The status changer itself, though, stays on the card too (2026-09-
 *  17 follow-up): the operator wants to change New→Contacted etc.
 *  without opening the drawer for every single beneficiary. The
 *  drawer keeps its own identical selector untouched — this is a
 *  second, independent way to do the same PATCH, not a replacement. */
/** Exported so the operator drill-down renders the SAME card as the
 *  beneficiary list. A lookalike would drift the moment either side
 *  changed (2026-09-27). */
export function LeadCard({
  lead,
  campaignId,
  onOpen,
}: {
  lead: Lead;
  campaignId: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const name = `${lead.contact_first_name} ${lead.contact_last_name}`.trim();
  const initial = (name[0] || "?").toUpperCase();

  const updateStatus = useMutation({
    mutationFn: async (status: LeadStatus) => {
      const res = await api.patch<Lead>(
        ApiPaths.campaignLead(campaignId, lead.id),
        { status },
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
    },
  });
  const secondary = [
    lead.contact_city,
    lead.contact_phone ? displayPhone(lead.contact_phone) : "",
  ].filter(Boolean);

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={
        "group flex cursor-pointer items-center gap-4 rounded-lg border border-nb-border bg-nb-bg/60 px-4 py-3 transition-colors hover:border-nb-mint/40 hover:bg-nb-surface/40 " +
        (lead.is_archived ? "opacity-60" : "")
      }
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-nb-mint/10 text-sm font-semibold text-nb-mint">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-nb-text">
            {name || "—"}
          </span>
          {lead.contact_is_business && (
            <span className="pill text-[10px]">
              {t("contacts.businessHeading", "Legal entity")}
            </span>
          )}
          {lead.is_archived ? (
            <LeadStatusPill status={lead.status} />
          ) : (
            <select
              value={lead.status}
              disabled={updateStatus.isPending}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                const next = e.target.value as LeadStatus;
                if (DECLINED_STATUSES.has(next)) {
                  // The "why declined" reason field only lives in the
                  // drawer — open it instead of half-applying the change.
                  onOpen();
                  return;
                }
                updateStatus.mutate(next);
              }}
              aria-label={
                name
                  ? `${t("leads.changeStatusLabel", "Change status")} — ${name}`
                  : t("leads.changeStatusLabel", "Change status")
              }
              className={
                "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium " +
                (STATUS_STYLES[lead.status] ?? STATUS_STYLES.new)
              }
            >
              {STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {t(`leads.status.${s}`)}
                </option>
              ))}
            </select>
          )}
          {/* Who reached them. Blank renders nothing — see
              ContactedBy.tsx for why that is not a third answer. */}
          <ContactedByBadge
            membership={lead}
            testId={`lead-contacted-by-${lead.id}`}
          />
          {lead.prefilled_answer_count > 0 && (
            <span
              className="pill text-[10px] text-nb-mint"
              title={t("leads.prefilledTooltip", {
                count: lead.prefilled_answer_count,
              })}
            >
              {t("leads.prefilledBadge", { count: lead.prefilled_answer_count })}
            </span>
          )}
          {/* Reported 2026-09-23 — "I've to show how many sites one
              beneficiary has on the listing as well." Only shown past
              one: a single site is the norm and needs no badge. */}
          {(lead.contact_site_count ?? 0) > 1 && (
            <span
              className="pill text-[10px] text-nb-text-2"
              title={t("leads.sitesTooltip", {
                count: lead.contact_site_count,
                defaultValue: "This beneficiary has {{count}} worksites",
              })}
            >
              {t("leads.sitesBadge", {
                count: lead.contact_site_count,
                defaultValue: "{{count}} sites",
              })}
            </span>
          )}
        </div>
        {secondary.length > 0 && (
          <p className="truncate text-xs text-nb-text-3">{secondary.join(" · ")}</p>
        )}
      </div>
      {lead.next_scheduled_for && (
        <div className="hidden shrink-0 text-right text-xs sm:block">
          <div className="font-medium text-nb-text">
            {formatDateTime(lead.next_scheduled_for)}
          </div>
        </div>
      )}
      <svg
        className="h-4 w-4 shrink-0 text-nb-text-3 transition-transform group-hover:translate-x-0.5"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 4.5l6 5.5-6 5.5" />
      </svg>
    </li>
  );
}


function RemoveLeadModal({
  lead,
  campaignId,
  onClose,
}: {
  lead: Lead;
  campaignId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reasonText, setReasonText] = useState("");
  const [conflict, setConflict] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async () => {
      await api.delete(ApiPaths.campaignLead(campaignId, lead.id), {
        data: { reason: reasonText.trim() },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
      onClose();
    },
    onError: (err) => {
      const e = err as {
        response?: { status?: number; data?: { detail?: string } };
      };
      if (e?.response?.status === 409 && e.response.data?.detail) {
        setConflict(e.response.data.detail);
      } else {
        setConflict(
          describeApiError(err, t("leads.removeErrorGeneric", "Could not remove")),
        );
      }
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("leads.removeTitle", "Remove from campaign")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel w-full max-w-md space-y-3">
        <h2 className="text-lg font-semibold">
          {t("leads.removeTitle", "Remove from campaign")}
        </h2>
        <p className="text-sm text-nb-text">
          {t("leads.removeBodyName", {
            name: `${lead.contact_first_name} ${lead.contact_last_name}`.trim(),
            defaultValue: "Remove {{name}} from this campaign?",
          })}
        </p>
        <p className="rounded-md border border-nb-mint/40 bg-nb-mint/10 px-3 py-2 text-xs text-nb-text">
          {t(
            "leads.removeKeepsContact",
            "Their contact details stay in your address book and can be re-enrolled later. To globally erase the person under GDPR, use the Contacts page.",
          )}
        </p>
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>
            {t("leads.removeReasonLabel", "Reason (optional, audit trail)")}
          </span>
          <textarea
            className="input min-h-[3.5rem]"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder={t(
              "leads.removeReasonPlaceholder",
              "Wrong campaign · duplicate · beneficiary declined …",
            )}
          />
        </label>
        {conflict && (
          <p className="rounded-md border border-cvp-warn/40 bg-cvp-warn/10 px-3 py-2 text-xs text-cvp-warn">
            {conflict}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onClose}
            disabled={remove.isPending}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            data-testid="lead-remove-confirm"
          >
            {remove.isPending
              ? t("leads.removing", "Removing…")
              : t("leads.removeButton", "Remove")}
          </button>
        </div>
      </div>
    </div>
  );
}


function NewLeadModal({
  campaignId,
  onClose,
}: {
  campaignId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<BeneficiaryForm>(emptyBeneficiaryForm);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  // Per-campaign (membership) custom-field values for this beneficiary.
  const [membershipFields, setMembershipFields] = useState<
    Record<string, string>
  >({});
  // Campaign-membership extras (not part of the shared beneficiary form).
  const [lead, setLead] = useState({ external_ref: "", notes_intake: "" });
  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<Lead>(ApiPaths.campaignLeads(campaignId), {
        ...beneficiaryPayload(form, customFields),
        membership_custom_fields: membershipFields,
        external_ref: lead.external_ref.trim(),
        notes_intake: lead.notes_intake.trim(),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
      void qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
      onClose();
    },
  });

  const canSubmit = canSubmitBeneficiary(form);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("leads.newLead")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel w-full max-w-2xl max-h-[90vh] space-y-3 overflow-y-auto">
        <h2 className="text-lg font-semibold">{t("leads.newLead")}</h2>
        <BeneficiaryFormFields
          form={form}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          customFields={customFields}
          onCustomFieldChange={(key, value) =>
            setCustomFields((d) => ({ ...d, [key]: value }))
          }
          campaign={campaignId}
        />
        {/* Per-campaign (membership) custom fields — renders only when the
            campaign declares any. */}
        <MembershipFieldsFieldset
          campaign={campaignId}
          values={membershipFields}
          onChange={(key, value) =>
            setMembershipFields((d) => ({ ...d, [key]: value }))
          }
        />
        {/* Campaign-specific extras. */}
        <section className="card space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t("leads.col.externalRef")}>
              <input
                className="input"
                value={lead.external_ref}
                onChange={(e) =>
                  setLead((l) => ({ ...l, external_ref: e.target.value }))
                }
              />
            </Field>
            <Field label={t("leads.col.notesIntake")} colSpan={2}>
              <textarea
                className="input min-h-[5rem]"
                value={lead.notes_intake}
                onChange={(e) =>
                  setLead((l) => ({ ...l, notes_intake: e.target.value }))
                }
              />
            </Field>
          </div>
        </section>
        {create.isError && (
          <p className="text-sm text-cvp-danger">
            {describeApiError(create.error, t("leads.createFailedGeneric"))}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({
  label,
  required,
  colSpan,
  children,
}: {
  label: string;
  required?: boolean;
  colSpan?: number;
  children: React.ReactNode;
}) {
  return (
    <label
      className={`space-y-1 text-xs text-nb-text-2 ${
        colSpan === 2 ? "col-span-2" : ""
      }`}
    >
      <span>
        {label}
        {required && <span className="ml-0.5 text-cvp-danger">*</span>}
      </span>
      {children}
    </label>
  );
}


function EditBeneficiaryModal({
  lead,
  campaignId,
  onClose,
}: {
  lead: Lead;
  campaignId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // This used to hand-roll a 6-field subset (name/email/phone/city/country) —
  // drifted from <BeneficiaryFormFields> (the single source of truth shared
  // by the edit page + both create modals) and silently dropped the
  // legal-entity block, contact-level custom fields, and the structured
  // address manager. Fetch the full Contact and reuse the same building
  // blocks as ContactEditPage so this modal can't drift again.
  const contact = useQuery({
    queryKey: ["contact", lead.contact],
    queryFn: async () => (await api.get<Contact>(ApiPaths.contact(lead.contact))).data,
  });

  // Same query key <MembershipFieldsCard> resolves internally — this just
  // decides whether to show the "Campaign fields" heading at all. A
  // campaign with no membership-scoped fields declared (e.g. this
  // campaign's custom data is all contact-scoped) would otherwise leave a
  // bare, empty heading with nothing under it.
  const membershipSchema = useQuery({
    queryKey: ["fields-schema", "membership", campaignId],
    queryFn: () => resolveFieldSchema({ applies_to: "membership", campaign: campaignId }),
  });
  const hasMembershipFields = (membershipSchema.data?.length ?? 0) > 0;

  const [form, setForm] = useState<BeneficiaryForm>(emptyBeneficiaryForm);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});

  useEffect(() => {
    const c = contact.data;
    if (!c) return;
    setForm(beneficiaryFormFromContact(c));
    setCustomFields(
      Object.fromEntries(
        Object.entries(c.custom_fields_jsonb || {}).map(([k, v]) => [
          k,
          String(v ?? ""),
        ]),
      ),
    );
  }, [contact.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch<Contact>(
        ApiPaths.contact(lead.contact),
        // Addresses are managed by the structured <ContactAddresses> manager
        // below (the source of truth); don't write flat address columns here.
        beneficiaryPayload(form, customFields, { includeAddress: false }),
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["contact", lead.contact], data);
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      onClose();
    },
  });

  const canSubmit = canSubmitBeneficiary(form);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("leads.editContact", "Edit contact")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel w-full max-w-2xl space-y-3">
        <h2 className="text-lg font-semibold">
          {t("leads.editContactTitle", "Edit beneficiary")}
        </h2>
        {contact.isLoading ? (
          <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
        ) : !contact.data ? (
          <p className="text-sm text-cvp-danger">{t("contacts.notFound")}</p>
        ) : (
          <>
            <BeneficiaryFormFields
              form={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              customFields={customFields}
              onCustomFieldChange={(key, value) =>
                setCustomFields((d) => ({ ...d, [key]: value }))
              }
              hideAddress
              campaign={campaignId}
            />
            {/* Structured, country-aware, multi-address manager — same one
                the standalone contact-edit page uses. Has its own <form> and
                saves immediately; not gated behind this modal's Save. */}
            <ContactAddresses contactId={lead.contact} isBusiness={form.is_business} />
            {/* Per-campaign (membership) custom fields — this modal used to
                have no way to edit these at all (only the contact-level
                fields above). Self-contained (own PATCH on Save), same card
                used on the Contact detail page. Only shown when this
                campaign actually declares membership-scoped fields — a
                campaign whose custom data is entirely contact-scoped would
                otherwise leave a bare heading with nothing under it. */}
            {hasMembershipFields && (
              <section className="card space-y-1">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
                  {t("leads.campaignFields.heading", "Campaign fields")}
                </h2>
                <MembershipFieldsCard
                  campaign={campaignId}
                  membershipId={lead.id}
                  values={lead.custom_fields_jsonb}
                  canEdit
                />
              </section>
            )}
          </>
        )}
        <p className="text-xs text-nb-text-3">
          {t(
            "leads.editContactNote",
            "Changes update the contact record across all campaigns.",
          )}
        </p>
        {save.isError && (
          <p className="text-sm text-cvp-danger">
            {describeApiError(
              save.error,
              t("leads.editContactErrorGeneric", "Could not save changes"),
            )}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={!contact.data || !canSubmit || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
